import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [twoFAState, setTwoFAState] = useState(null); // null | 'setup_required' | 'verify_required' | 'verified'

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);

  useEffect(() => {
    const checkAuth = async () => {
      if (token) {
        try {
          const response = await axios.get(`${API}/auth/me`);
          setUser(response.data);
          setTwoFAState('verified');
        } catch (error) {
          // 403 means token is valid but 2FA not done
          if (error.response?.status === 403) {
            const detail = error.response?.data?.detail || '';
            // Parse the JWT to get user info
            try {
              const payload = JSON.parse(atob(token.split('.')[1]));
              setUser({ id: payload.sub, email: payload.email, role: payload.role });
            } catch (e) {
              // ignore parse error
            }
            if (detail.includes('setup required')) {
              setTwoFAState('setup_required');
            } else {
              setTwoFAState('verify_required');
            }
          } else {
            localStorage.removeItem('token');
            setToken(null);
            setUser(null);
            setTwoFAState(null);
          }
        }
      }
      setLoading(false);
    };
    checkAuth();
  }, [token]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user: userData, requires_2fa_setup, requires_2fa_verify } = response.data;
    localStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(userData);

    if (requires_2fa_setup) {
      setTwoFAState('setup_required');
    } else if (requires_2fa_verify) {
      setTwoFAState('verify_required');
    } else {
      // No 2FA at all (shouldn't happen with mandatory, but handle gracefully)
      setTwoFAState('setup_required');
    }

    return { userData, requires_2fa_setup, requires_2fa_verify };
  };

  const register = async (email, password, name) => {
    const response = await axios.post(`${API}/auth/register`, { email, password, name });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(userData);
    setTwoFAState('setup_required');
    return userData;
  };

  const updateToken = (newToken) => {
    localStorage.setItem('token', newToken);
    setToken(newToken);
    setTwoFAState('verified');
    // Re-fetch user info with the verified token
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    axios.get(`${API}/auth/me`).then(res => setUser(res.data)).catch(() => {});
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    setTwoFAState(null);
  };

  const value = {
    user,
    token,
    loading,
    login,
    register,
    logout,
    updateToken,
    twoFAState,
    isAuthenticated: !!user && twoFAState === 'verified',
    isAdmin: user?.role === 'admin'
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
